import axios from 'axios';
import { TRADING_API } from '../config/constants'; // for some reason, during "yarn build", 'config' does not resolve, whereas '../config' does
import { actionOutcome } from '../lib/action-outcome';

// Central handler for an expired/unauthorized session (HTTP 401). The
// authentication context registers a handler here (styled alert + logout) via
// setUnauthorizedHandler. We deliberately do NOT import the authentication
// context into this module, because the context already imports from here and
// doing so would create a circular dependency.
let unauthorizedHandler = null;

// Every arm/disarm of the handler starts a new "session generation". A request
// remembers the generation it was sent under, so a 401 can be attributed to the
// session it actually belongs to. Without this, a request that was already in
// flight while logged out (the account poller on the login screen) could land
// its 401 just after a login completed and tear down the brand new session.
// Passkey logins are hit by this the hardest: the Face ID / Touch ID ceremony
// keeps the app logged out for seconds while those requests are pending.
let sessionGeneration = 0;

export const setUnauthorizedHandler = handler => {
	unauthorizedHandler = handler;
	sessionGeneration++;
};

// Hands a 401 to the session handler: once, and only for a request sent under the current session
const notifyUnauthorized = requestGeneration => {
	if (requestGeneration === sessionGeneration) {
		const handler = unauthorizedHandler;
		unauthorizedHandler = null;
		if (handler) {
			handler();
		}
	}
};

const mapCandlePricesToProperties = item => ({
	Date: 1000 * item[0],
	Low: item[1],
	High: item[2],
	Open: item[3],
	Close: item[4],
	Volume: item[5],
});

const apiRequest = async (uri, params = {}, returnId) => {
	let data;
	const requestGeneration = sessionGeneration;
	try {
		if (params.exchange) {
			params.exchange = params.exchange.toLowerCase();
		}

		console.debug(`${TRADING_API}/${uri}`, params, {
			headers: {
				Accept: 'application/json',
				'Content-type': 'application/json',
			},
			withCredentials: true, // make sure to send the cookies along
		});

		const response = await axios.post(`${TRADING_API}/${uri}`, params, {
			headers: {
				Accept: 'application/json',
				'Content-type': 'application/json',
			},
			withCredentials: true, // make sure to send the cookies along
		});
		if (!response || response.status !== 200 || !response.data) {
			const err = `Error: ${response.status}: ${response.statusText}`;
			// throw new Error(err);
			console.error(err);
		}

		if (uri === 'logout') {
			data = response.data.success;
		} else if (returnId) {
			data = response.data?.id || response.data?.data?.id;
		} else {
			data = response.data?.data;
		}
	} catch (err) {
		if (err?.response?.status === 401) {
			// Session/cookie expired or the request was unauthorized. Hand off to
			// the central handler (styled alert + logout), but only when this
			// request was sent under the session that is still current: a 401 for
			// a request from an older generation says nothing about this session.
			// Grab-and-clear it so concurrent 401s from parallel polls only trigger
			// the flow once; the authentication context re-arms it on the next
			// successful login.
			notifyUnauthorized(requestGeneration);
		} else {
			console.error(err);
		}
	}

	return data;
};

// For an action the user waits on: answers whether it worked and, if not, why (see lib/action-outcome),
// where apiRequest only logs a failure. A 401 still goes to the session handler.
const apiAction = async (uri, params = {}) => {
	const requestGeneration = sessionGeneration;
	if (params.exchange) {
		params.exchange = params.exchange.toLowerCase();
	}
	try {
		const response = await axios.post(`${TRADING_API}/${uri}`, params, {
			headers: {
				Accept: 'application/json',
				'Content-type': 'application/json',
			},
			withCredentials: true, // make sure to send the cookies along
		});
		return actionOutcome({ status: response.status, body: response.data });
	} catch (err) {
		if (err?.response?.status === 401) {
			notifyUnauthorized(requestGeneration);
		} else {
			console.error(err);
		}
		return actionOutcome({ status: err?.response?.status, body: err?.response?.data, message: err?.message });
	}
};

export const getPrice = async ({ symbol }) => apiRequest('get-price', { symbol });

export const getBalances = async ({ exchange, network, type, symbol }) =>
	apiRequest('get-balances', { exchange, network, type, symbol });

export const getCandlePrices = async ({ asset, interval, count, from }) => {
	const candlePrices = await apiRequest('candleprices', { asset, interval, count, from });
	if (!candlePrices) {
		return [];
	}
	return candlePrices.map(mapCandlePricesToProperties);
};

export const getPositions = async ({ exchange, network, type, onlyFromDatabase, force }) => {
	let positions;
	try {
		positions = await apiRequest('get-positions', { exchange, network, type, onlyFromDatabase, force });
	} catch (err) {
		console.error('OOPS', err);
		positions = [];
	}
	if (!Array.isArray(positions)) {
		positions = [];
	}
	return positions;
};

export const getClosedPositions = async ({ exchange, network, type, symbol, limit, offset }) => {
	try {
		return await apiRequest('get-closed-positions', { exchange, network, type, symbol, limit, offset });
	} catch (err) {
		console.error('Error fetching closed positions:', err);
		return [];
	}
};

export const getOpenOrders = async ({ exchange, network, type, symbol }) =>
	apiRequest('get-open-orders', { exchange, network, type, symbol });

export const getStops = async ({ exchange, network, type }) => apiRequest('get-stops', { exchange, network, type });

export const getTradesHistory = async ({ exchange, network, symbol, limit, offset }) =>
	apiRequest('trades-history', { exchange, network, symbol, limit, offset });

export const getScope = async ({ account, password }) => {
	const data = await apiRequest('authenticate', { account, password });
	return data?.scope;
};

// ---- Passkey (WebAuthn) ----

// Login: fetch usernameless authentication options + the server-side challenge nonce.
export const getPasskeyAuthOptions = async () => apiRequest('passkey-auth-options');

// Login: submit the assertion; returns the scope (truthy) on success.
export const verifyPasskeyAuth = async ({ credential, nonce }) => {
	const data = await apiRequest('passkey-auth-verify', { credential, nonce });
	return data?.scope;
};

// Enrollment (must be logged in): fetch registration options.
export const getPasskeyRegisterOptions = async () => apiRequest('passkey-register-options');

// Enrollment: submit the attestation; returns a truthy object on success.
export const verifyPasskeyRegister = async ({ credential, deviceLabel }) =>
	apiRequest('passkey-register-verify', { credential, deviceLabel });

export const listPasskeys = async () => apiRequest('passkey-list');

export const removePasskey = async ({ id }) => apiRequest('passkey-remove', { id });

export const getNewTradeStats = async ({ exchange, symbol, amountWholeUSDC, leverage, isLong, useCache }) => {
	const data = await apiRequest('get-new-trade-stats', {
		exchange: exchange.name,
		network: exchange.network,
		symbol,
		isLong,
		amountWholeUSDC,
		leverage,
		useCache,
	});

	if (data?.amountInUSD) {
		data.amountInUSD = BigInt(data.amountInUSD);
		data.amountOutUSD = BigInt(data.amountOutUSD);
		data.lentUSD = BigInt(data.lentUSD);
		data.amountOut = BigInt(data.amountOut);
		data.entryPriceUSD = BigInt(data.entryPriceUSD);
		data.breakEvenPriceUSD = BigInt(data.breakEvenPriceUSD);
		data.liquidationPriceUSD = BigInt(data.liquidationPriceUSD);
		data.feeUSD = BigInt(data.feeUSD);

		if (data.stopLossPriceUSD) {
			data.stopLossPriceUSD = BigInt(data.stopLossPriceUSD);
		}
		if (data.stopLossesUSD) {
			data.stopLossesUSD = BigInt(data.stopLossesUSD);
		}
		if (data.takeProfitPriceUSD) {
			data.takeProfitPriceUSD = BigInt(data.takeProfitPriceUSD);
			data.takeProfitUSD = BigInt(data.takeProfitUSD);
		}
		data.hourlyInterestUSD = BigInt(data.hourlyInterestUSD);
		data.liquidity = BigInt(data.liquidity);
	}

	return data;
};

//= ======================================================================================

export const closeMarginLong = async ({ exchange, network, marginModel, symbol, price }) =>
	apiRequest('close-margin-long', { exchange, network, marginModel, symbol, price });

export const closeMarginShort = async ({ exchange, network, marginModel, symbol, price }) =>
	apiRequest('close-margin-short', { exchange, network, marginModel, symbol, price });

export const marginLong = async ({
	exchange,
	network,
	marginModel,
	symbol,
	amountWholeUSDC,
	leverage,
	price,
	takeProfitAtUsd,
	stoploss,
	isTriggerOrder,
	takeProfitAtUsdLimitPrice,
	stoplossLimitPrice,
	retainOpenOrderTime,
}) =>
	apiRequest('margin-long', {
		exchange,
		marginModel,
		network,
		symbol,
		amountWholeUSDC,
		leverage,
		price,
		takeProfitAtUsd,
		stoploss,
		isTriggerOrder,
		takeProfitAtUsdLimitPrice,
		stoplossLimitPrice,
		retainOpenOrderTime,
	});

export const marginShort = async ({
	exchange,
	network,
	marginModel,
	symbol,
	amountWholeUSDC,
	leverage,
	price,
	takeProfitAtUsd,
	stoploss,
	isTriggerOrder,
	takeProfitAtUsdLimitPrice,
	stoplossLimitPrice,
	retainOpenOrderTime,
}) =>
	apiRequest('margin-short', {
		exchange,
		marginModel,
		network,
		symbol,
		amountWholeUSDC,
		leverage,
		price,
		takeProfitAtUsd,
		stoploss,
		isTriggerOrder,
		takeProfitAtUsdLimitPrice,
		stoplossLimitPrice,
		retainOpenOrderTime,
	});

export const canCancelOrder = async ({ exchange, network, action, symbol }) =>
	apiRequest('can-cancel-action', { exchange, network, action, symbol });

export const cancelOrder = async ({ exchange, network, orderid, symbol }) =>
	apiRequest('cancel-action', { exchange, network, orderid, symbol });

// The open-orders list cancels through this one, so it can tell the user why a cancel failed
export const cancelOpenOrder = async ({ exchange, network, orderid, symbol }) =>
	apiAction('cancel-action', { exchange, network, orderid, symbol });

// The AI Optimise tab reads its runs through apiAction, so the reason of a refusal reaches the user
export const getAiOptimizations = async ({ strategyid, limit, offset }) =>
	apiAction('get-ai-optimizations', { strategyid, limit, offset });

// The strategy id and the run's stop-loss (a number from lib/ai-optimization RUN_STOPLOSS_CHOICES; trading-api refuses any
// other with HTTP 400) are sent: the rounds, the time budget and the threads of a run come from the simulator's own config
export const createAiOptimization = async ({ strategyid, stoploss }) =>
	apiAction('create-ai-optimization', { strategyid, stoploss });

export const cancelAiOptimization = async ({ id }) => apiAction('cancel-ai-optimization', { id });

// One run with everything stored on it (patch, stats, explanation, trace, funnel, stability, metadata)
export const getAiOptimization = async ({ id }) => apiAction('get-ai-optimization', { id });

// The server applies the stored suggestion of the run by its id; the browser never sends a configuration or a stop-loss
// value, only applystoploss true when the user ticked "also set stop-loss" (trading-api then writes the run's own stored one)
export const applyAiOptimization = async ({ id, applystoploss }) =>
	apiAction('apply-ai-optimization', applystoploss === true ? { id, applystoploss: true } : { id });

// The admin header block shows the OpenAI spend (admin scope); apiAction, so the reason of a refusal reaches the block
export const getOpenAiSpend = async () => apiAction('get-openai-spend', {});

export const setStopLossLong = async ({ exchange, network, stopPriceUsd, symbol, priceAvailable }) =>
	apiRequest(priceAvailable ? 'update-stop-loss-long' : 'set-stop-loss-long', {
		exchange,
		network,
		stopPriceUsd,
		symbol,
	});

export const setStopLossShort = async ({ exchange, network, stopPriceUsd, symbol, priceAvailable }) =>
	apiRequest(priceAvailable ? 'update-stop-loss-short' : 'set-stop-loss-short', {
		exchange,
		network,
		stopPriceUsd,
		symbol,
	});

export const setTakeProfitLong = async ({ exchange, network, stopPriceUsd, symbol, priceAvailable }) =>
	apiRequest(priceAvailable ? 'update-take-profit-long' : 'set-take-profit-long', {
		exchange,
		network,
		stopPriceUsd,
		symbol,
	});

export const setTakeProfitShort = async ({ exchange, network, stopPriceUsd, symbol, priceAvailable }) =>
	apiRequest(priceAvailable ? 'update-take-profit-short' : 'set-take-profit-short', {
		exchange,
		network,
		stopPriceUsd,
		symbol,
	});

export const getMarketdata = async ({ exchange, network }) =>
	apiRequest('get-marketdata', {
		exchange,
		network,
	});

export const swapTokens = async ({ exchange, network, fromSymbol, toSymbol, amountFloat }) =>
	apiRequest('swap-tokens', { exchange, network, fromSymbol, toSymbol, amountFloat });

export const getStrategies = async ({ active, showperformance, performancelimit } = {}) =>
	apiRequest('get-strategies', { active, showperformance, performancelimit });

export const updateStrategy = async ({
	id,
	name,
	asset,
	assettype,
	version,
	interval,
	strategyenter,
	strategyexit,
	stoplosspercent,
	preventcloseonprofitcandle,
	panicstrategy,
	panicstrategyvalue,
	preventclosewithinfirstcandles,
	preventcloseonpricelost,
	closeonimmediatereveralactivated,
	closeonimmediatereveral,
	closeonimmediatereveralarea,
	closeonpricereversal,
	prevententeronextremepricechange,
	prevententeronextremepricechangevalue,
	prevententeronextremepricearea,
	preventcloseonpositioninotherdirection,
	stoplossonlyontwosequentcandles,
	usetrailingprofit,
	trailingprofit,
	trailingprofitinitiation,
	optimumtradeamountpercent,
	notradeswithincandlesafterlasttrade,
	notradeswithincandlesafterlasttradevalue,
	preventcloseonpricelostperiod,
	preventcloseonpricelostperiodvalue,
	minprofit,
	punishbadtrades,
	force,
}) =>
	apiRequest('update-strategy', {
		id,
		name,
		asset,
		assettype,
		version,
		interval,
		strategyenter,
		strategyexit,
		stoplosspercent,
		preventcloseonprofitcandle,
		panicstrategy,
		panicstrategyvalue,
		preventclosewithinfirstcandles,
		preventcloseonpricelost,
		closeonimmediatereveralactivated,
		closeonimmediatereveral,
		closeonimmediatereveralarea,
		closeonpricereversal,
		prevententeronextremepricechange,
		prevententeronextremepricechangevalue,
		prevententeronextremepricearea,
		preventcloseonpositioninotherdirection,
		stoplossonlyontwosequentcandles,
		usetrailingprofit,
		trailingprofit,
		trailingprofitinitiation,
		optimumtradeamountpercent,
		notradeswithincandlesafterlasttrade,
		notradeswithincandlesafterlasttradevalue,
		preventcloseonpricelostperiod,
		preventcloseonpricelostperiodvalue,
		minprofit,
		punishbadtrades,
		force,
	});

export const updateStrategyName = async ({ id, name }) => apiRequest('update-strategy-name', { id, name });

export const createStrategy = async ({
	name,
	asset,
	assettype,
	version,
	interval,
	strategyenter,
	strategyexit,
	stoplosspercent,
	preventcloseonprofitcandle,
	panicstrategy,
	panicstrategyvalue,
	preventclosewithinfirstcandles,
	preventcloseonpricelost,
	closeonimmediatereveralactivated,
	closeonimmediatereveral,
	closeonimmediatereveralarea,
	closeonpricereversal,
	prevententeronextremepricechange,
	prevententeronextremepricechangevalue,
	prevententeronextremepricearea,
	preventcloseonpositioninotherdirection,
	stoplossonlyontwosequentcandles,
	usetrailingprofit,
	trailingprofit,
	trailingprofitinitiation,
	optimumtradeamountpercent,
	notradeswithincandlesafterlasttrade,
	notradeswithincandlesafterlasttradevalue,
	preventcloseonpricelostperiod,
	preventcloseonpricelostperiodvalue,
	punishbadtrades,
	minprofit,
}) =>
	apiRequest(
		'create-strategy',
		{
			name,
			asset,
			assettype,
			version,
			interval,
			strategyenter,
			strategyexit,
			stoplosspercent,
			preventcloseonprofitcandle,
			panicstrategy,
			panicstrategyvalue,
			preventclosewithinfirstcandles,
			preventcloseonpricelost,
			closeonimmediatereveralactivated,
			closeonimmediatereveral,
			closeonimmediatereveralarea,
			closeonpricereversal,
			prevententeronextremepricechange,
			prevententeronextremepricechangevalue,
			prevententeronextremepricearea,
			preventcloseonpositioninotherdirection,
			stoplossonlyontwosequentcandles,
			usetrailingprofit,
			trailingprofit,
			trailingprofitinitiation,
			optimumtradeamountpercent,
			notradeswithincandlesafterlasttrade,
			notradeswithincandlesafterlasttradevalue,
			preventcloseonpricelostperiod,
			preventcloseonpricelostperiodvalue,
			minprofit,
			punishbadtrades,
		},
		true,
	);

export const activateStrategy = async ({ id, active }) => apiRequest('activate-strategy', { id, active });

export const removeStrategy = async ({ strategyid }) => apiRequest('remove-strategy', { strategyid }, true);

//= ======================================================================================

export const accountGetSessionUser = async () => apiRequest('account-get-session-user');

export const accountActivation = async ({ accounthash, active }) =>
	apiRequest('account-activation', { accounthash, active });

export const accountChangeExchangeKey = async ({ exchange, exchangeKeys }) =>
	apiRequest('account-change-exchange-key', { exchange, exchangeKeys });

export const accountChangeExchangeEnterPosition = async ({ exchange, exchangeEnterPosition }) =>
	apiRequest('account-change-exchange-enterposition', { exchange, exchangeEnterPosition });

export const accountChangeExchangeTradeSettings = async ({
	exchange,
	margintradereserves,
	ordertypetrades,
	ordertypestoploss,
	overbidlimittrades,
	overbidlimitstoploss,
	strategytradesresultcorrection,
}) =>
	apiRequest('account-change-exchange-settings', {
		exchange,
		margintradereserves,
		ordertypetrades,
		ordertypestoploss,
		overbidlimittrades,
		overbidlimitstoploss,
		strategytradesresultcorrection,
	});

export const accountChangePassword = async ({ oldPassword, newPassword }) =>
	apiRequest('account-change-password', { oldPassword, newPassword });

export const accountChangeUsername = async ({ accountname }) => apiRequest('account-change-username', { accountname });

export const accountChangeRole = async ({ accounthash, role }) =>
	apiRequest('account-change-role', { accounthash, role });

export const accountList = async () => apiRequest('account-list');

export const accountNew = async ({ accountname, password, role }) =>
	apiRequest('account-new', { accountname, password, role }, true);

export const accountRemoval = async ({ accounthash }) => apiRequest('account-removal', { accounthash }, true);

export const accountStrategyList = async ({ showperformance, performancelimit, exchange, network } = {}) =>
	apiRequest('account-strategy-list', { showperformance, performancelimit, exchange, network });

export const accountStrategyNew = async ({ strategyid, type, symbol, exchange, network, leverage, percentamount }) =>
	apiRequest(
		'account-strategy-new',
		{
			strategyid,
			type,
			symbol,
			exchange,
			network,
			leverage,
			percentamount,
		},
		true,
	);

export const accountStrategyChange = async ({
	accountstrategyid,
	strategyid,
	type,
	symbol,
	exchange,
	network,
	leverage,
	percentamount,
}) =>
	apiRequest('account-strategy-change', {
		accountstrategyid,
		strategyid,
		type,
		symbol,
		exchange,
		network,
		leverage,
		percentamount,
	});

export const accountStrategyActivation = async ({ accountstrategyid, active }) =>
	apiRequest('account-strategy-activation', { accountstrategyid, active });

export const accountStrategyRemoval = async ({ accountstrategyid }) =>
	apiRequest('account-strategy-removal', { accountstrategyid }, true);

export const getStrategySimulations = async ({ finished } = {}) => {
	const records = await apiRequest('get-strategy-simulations', { finished });
	if (!records) {
		return [];
	}
	records.forEach(record => {
		if (record.starttime) {
			record.starttime = new Date(record.starttime);
		}
		if (record.stoptime) {
			record.stoptime = new Date(record.stoptime);
		}
		if (record.eta) {
			record.eta = new Date(record.eta);
		}
	});
	return records;
};

export const getStrategySimulationDetails = async ({ strategysimulationid, limit, sortby }) =>
	apiRequest('get-strategy-simulation-details', { strategysimulationid, limit, sortby });

export const removeStrategySimulation = async ({ simulationid }) => apiRequest('remove-simulation', { simulationid });

export const restartStrategySimulation = async ({ simulationid }) => apiRequest('restart-simulation', { simulationid });

export const releaseSimulation = async ({ simulationid, processedby, force }) =>
	apiRequest('release-simulation', { simulationid, processedby, force });

export const defineStrategySimulation = async ({ strategyid, simulate, stoploss, candles }) =>
	apiRequest('define-simulation', { strategyid, simulate, stoploss, candles });

export const getSimulatorStatus = async () => {
	const status = await apiRequest('get-simulator-status');
	return status || [];
};

//= ======================================================================================

export const ping = async () => {
	const data = await apiRequest('ping');
	return data?.scope;
};

export const logout = async () => {
	let response;
	try {
		response = await apiRequest('logout');
	} catch (err) {
		console.debug('ERROR LOGOUT', err);
	}
	return response;
};
